import { CONSTANTS, StringUtils } from '@pk/powerkioskutils';

import _ from 'lodash';
import moment from 'moment-timezone';

import { DatePipe } from '@angular/common';
import { environment } from 'src/environments/environment';
import { HelperService } from '../helper.service';
import { ServiceLocator } from '../service-locator.service';
import { Contract } from './contract';
import { RfqSessionBid } from './rfq-session-bid';
import { RfqSessionBidTrendAnalysis } from './rfq-session-bid-trend-analysis';
import { RfqSessionLatestPricingComponent } from './rfq-session-latest-pricing-component';
import { RfqSessionProduct } from './rfq-session-product';
import { RfqSessionStat } from './rfq-session-stat';
import { RfqSessionSupplier } from './rfq-session-supplier';

export class CustomRfqSession {
	public rfqSessionId: string;
	public startDate: Date;
	public endDate: Date;
	public activeTotalNumBids: number;
	public activeTotalNumParticipants: number;
	public supplierId: string;
	public note: string;
	public contractId: string;
	public contractNum: string;
	public contractStatus: string;
	public annualUsage: string | number;
	public customerId: string;
	public dbaName: string;
	public lowerDbaName: string;
	public utilityAccountNumList: string | string[];
	public utilityIdList: string | string[];
	public utilityNameList: string | string[];
	public serviceTypeId: string;
	public serviceTypeName: string;
	public sampleBillAttachmentIdList: string | string[];
	public hudAttachmentIdList: string | string[];
	public hudConsolidatedAttachmentIdList: string | string[];
	public loaAttachmentIdList: string | string[];
	public loaEsignIdList: string | string[];
	public loaHasEsignList: string | boolean[];
	public loeAttachmentIdList: string | string[];
	public loeEsignIdList: string | string[];
	public loeHasEsignList: string | boolean[];
	public effectiveDate: Date;
	public uploadDate: Date;
	public awardDate: Date;
	public units: string;
	public rfqSessionSupplierId: string;
	public isOptOut: boolean;
	public sampleBillRequestDate: Date;
	public loaRequestDate: Date;
	public loeRequestDate: Date;
	public stateId: string;
	public agentId: string;
	public loadFactor: number;
	public hasUsage: boolean;
	public displayAnnualUsage: number;
	public displayAnnualUsageUnits: string;
	public isSaas: boolean;
	public lastBidDate: Date;
	public locationCount: number;
	public rfqSessionStatus: string;
	public numParticipants: number;
	public isPinned: boolean;
	public instructions: string;

	public optInText = 'Opt Back In';
	public optInDisabled = false;

	public isSupplierUser: boolean;
	public timeRemainingDisplay: string;
	public rfqSessionStatusColor: string;
	public formattedStartDate: string;
	public formattedEndDate: string;
	public formattedLastBidDate: string;
	public timeRemaining: { days: number; hours: number; minutes: number; seconds: number };
	public lastBidDays: { [key: string]: string[] }; // for now max out at 4
	public lastBidTime: Date;

	public suppliers: RfqSessionSupplier[];
	public bids: RfqSessionBid[];
	public products: RfqSessionProduct[];
	public contract: Contract;
	public bestBids: RfqSessionStat[];
	public trendAnalysis: RfqSessionBidTrendAnalysis[];
	public latestPricingComponents: RfqSessionLatestPricingComponent[];

	constructor(rfqSession: CustomRfqSession, isSupplierUser = false, rfqSessionSupplierId?: string) {
		Object.assign(this, rfqSession);

		this.startDate = this.startDate ? new Date(moment(this.startDate).format('MMM DD, YYYY hh:mm:ss a')) : this.startDate;
		this.endDate = this.endDate ? new Date(moment(this.endDate).format('MMM DD, YYYY hh:mm:ss a')) : this.endDate;
		this.effectiveDate = this.effectiveDate ? new Date(moment(this.effectiveDate).format('MMM DD, YYYY 00:00:00 a')) : this.effectiveDate;
		this.uploadDate = this.uploadDate ? new Date(moment(this.uploadDate).format('MMM DD, YYYY hh:mm:ss a')) : this.uploadDate;
		this.awardDate = this.awardDate ? new Date(moment(this.awardDate).format('MMM DD, YYYY hh:mm:ss a')) : this.awardDate;
		this.sampleBillRequestDate = this.sampleBillRequestDate
			? new Date(moment(this.sampleBillRequestDate).format('MMM DD, YYYY hh:mm:ss a'))
			: this.sampleBillRequestDate;
		this.loeRequestDate = this.loeRequestDate ? new Date(moment(this.loeRequestDate).format('MMM DD, YYYY hh:mm:ss a')) : this.loeRequestDate;
		this.loaRequestDate = this.loaRequestDate ? new Date(moment(this.loaRequestDate).format('MMM DD, YYYY hh:mm:ss a')) : this.loaRequestDate;
		this.isSupplierUser = isSupplierUser;
		this.utilityIdList = this.utilityIdList ? (this.utilityIdList as string).split(',') : [];
		this.utilityNameList = this.utilityNameList ? (this.utilityNameList as string).split(',') : [];
		this.utilityAccountNumList = this.utilityAccountNumList ? (this.utilityAccountNumList as string).split(',') : [];
		this.sampleBillAttachmentIdList = this.sampleBillAttachmentIdList ? (this.sampleBillAttachmentIdList as string).split(',') : [];
		this.hudAttachmentIdList = this.hudAttachmentIdList ? (this.hudAttachmentIdList as string).split(',') : [];
		this.hudConsolidatedAttachmentIdList = this.hudConsolidatedAttachmentIdList ? (this.hudConsolidatedAttachmentIdList as string).split(',') : [];
		this.loaAttachmentIdList = this.loaAttachmentIdList ? (this.loaAttachmentIdList as string).split(',') : [];
		this.loaEsignIdList = this.loaEsignIdList ? (this.loaEsignIdList as string).split(',') : [];
		this.loaHasEsignList = this.loaHasEsignList ? (this.loaHasEsignList as string).split(',').map(e => StringUtils.toBoolean(e)) : [];
		this.loeAttachmentIdList = this.loeAttachmentIdList ? (this.loeAttachmentIdList as string).split(',') : [];
		this.loeEsignIdList = this.loeEsignIdList ? (this.loeEsignIdList as string).split(',') : [];
		this.loeHasEsignList = this.loeHasEsignList ? (this.loeHasEsignList as string).split(',').map(e => StringUtils.toBoolean(e)) : [];
		this.lowerDbaName = this.dbaName ? this.dbaName.toLowerCase() : this.dbaName;
		this.lastBidDate =
			rfqSessionSupplierId && this.bids?.filter(bid => bid.rfqSessionSupplierId === rfqSessionSupplierId).length
				? new Date(
					moment(
						_.orderBy(
							this.bids.filter(bid => bid.rfqSessionSupplierId === rfqSessionSupplierId),
							b => b.addDate,
							'desc',
						)[0].addDate,
					).format('MMM DD, YYYY hh:mm:ss a'),
				)
				: this.lastBidDate;

		this.contract = this.contract ? new Contract(this.contract) : this.contract;
		this.products = this.products ? this.products.map(p => new RfqSessionProduct(p)) : this.products;
		this.suppliers = this.suppliers ? this.suppliers.map(s => new RfqSessionSupplier(s)) : this.suppliers;
		this.bids = this.bids ? this.bids.map(b => new RfqSessionBid(b)) : this.bids;
		this.bestBids = this.bestBids ? this.bestBids.map(b => new RfqSessionStat(b)) : this.bestBids;
		this.setupProductsAndBids();

		this.formattedStartDate = this.formatStartDate();
		this.formattedEndDate = this.formatEndDate();
		this.formattedLastBidDate = this.formatLastBidDate();
		this.setupSessionStatus();

		// as long as we're not completed, update every second
		if (this.timeRemainingDisplay !== '0d 0h 0m') {
			const interval = setInterval(() => {
				this.setupSessionStatus();
				if (this.timeRemainingDisplay === '0d 0h 0m') {
					clearInterval(interval);
				}
			}, 1000);
		}
	}

	get energyIcon(): string {
		return HelperService.getEnergyIcon(this.serviceTypeId);
	}

	public setupProductsAndBids(): void {
		const datePipe = ServiceLocator.injector.get(DatePipe);
		this.lastBidDays = {};
		if (this.bids && this.bids.length) {
			this.bids.forEach(bid => {
				bid.rfqSessionProduct =
					this.products && this.products.length ? this.products.find(p => p.rfqSessionProductId === bid.rfqSessionProductId) : null;
				if (!this.lastBidTime || (bid.addDate as Date).valueOf() > this.lastBidTime.valueOf()) {
					this.lastBidTime = bid.addDate as Date;
				}
			});

			// sort the bids by addDate then add them to the lastbiddays list
			_.chain(this.bids)
				.groupBy(b => b.rfqSessionSupplierId)
				.toPairs()
				.map(g => {
					if (!this.lastBidDays[g[0]]) {
						this.lastBidDays[g[0]] = [];
					}
					_.orderBy(g[1], [b => b.addDate.valueOf()], ['desc']).forEach(b => {
						const bidDate = datePipe.transform(b.addDate, 'MMM dd, yyyy');
						if (!this.lastBidDays[g[0]].includes(bidDate) && this.lastBidDays[g[0]].length < 4) {
							this.lastBidDays[g[0]].push(bidDate);
						}
					});
					this.lastBidDays[g[0]] = _.reverse(this.lastBidDays[g[0]]);
				})
				.value();
		}

		if (this.products && this.products.length) {
			this.products.forEach(product => {
				product.bestBids =
					this.bestBids && this.bestBids.length
						? _.orderBy(
							this.bestBids.filter(b => b.rfqSessionProductId === product.rfqSessionProductId),
							'rank',
						)
						: null;
				product.bids =
					this.bids && this.bids.length
						? _.orderBy(
							this.bids.filter(b => b.rfqSessionProductId === product.rfqSessionProductId),
							[b => b.addDate.valueOf()],
							['desc'],
						)
						: [];

				// grab the best rate for each of the last bid days for the product
				product.bidsAnalysis = {};
				_.chain(product.bids)
					.groupBy(b => b.rfqSessionSupplierId)
					.toPairs()
					.map(g => {
						_.chain(g[1])
							.groupBy(b => datePipe.transform(b.addDate, 'MMM dd, yyyy'))
							.toPairs()
							.filter(b => this.lastBidDays[g[0]].includes(b[0]))
							.map(gb => {
								if (!product.bidsAnalysis[g[0]]) {
									product.bidsAnalysis[g[0]] = {};
								}
								product.bidsAnalysis[g[0]][gb[0]] = _.orderBy(gb[1], a => a.displayRate)[0];
							})
							.value();
					})
					.value();

				// grab the latest rates per supplier per product
				product.latestBids = {};
				product.latestBidDays = {};
				_.chain(product.bids)
					.groupBy(b => b.rfqSessionSupplierId)
					.toPairs()
					.map(g => {
						_.chain(g[1])
							.groupBy(b => datePipe.transform(b.addDate, 'MMM dd, yyyy'))
							.toPairs()
							.orderBy(gb => new Date(gb[0]).getTime(), 'desc')
							.map(gb => {
								if (!product.latestBids[g[0]]) {
									product.latestBidDays[g[0]] = [gb[0]];
									product.latestBids[g[0]] = { [gb[0]]: gb[1][0] };
								} else {
									if (product.latestBidDays[g[0]].length < 3) {
										product.latestBidDays[g[0]].unshift(gb[0]);
										product.latestBids[g[0]][gb[0]] = gb[1][0];
									}
								}
							})
							.value();
					})
					.value();
			});
		}

		if (this.suppliers && this.suppliers.length) {
			this.suppliers.forEach(supplier => {
				supplier.bids = this.bids && this.bids.length ? this.bids.filter(b => b.rfqSessionSupplierId === supplier.rfqSessionSupplierId) : [];
			});
		}
	}

	get loaAvailable(): boolean {
		return typeof this.loaHasEsignList === 'object' && (this.loaHasEsignList as boolean[]).includes(true);
	}

	private setupSessionStatus(): void {
		this.timeRemaining = this.getTimeRemaining();
		this.timeRemainingDisplay = this.getTimeRemainingDisplay();
		this.rfqSessionStatusColor = this.getRfqSessionStatusColor();
	}

	private getTimeRemaining(): { days: number; hours: number; minutes: number; seconds: number } {
		if (!this.endDate) {
			return null;
		}

		const now = moment();
		const endDate = moment(this.endDate);

		const offset = moment().utcOffset();
		moment.tz.setDefault();
		const localOffset = moment().utcOffset();
		moment.tz.setDefault(environment.timezone);

		const offsetDiff = offset - localOffset;
		endDate.subtract(offsetDiff, 'minutes');

		if (!['Extended', 'Closed', 'Won'].includes(this.rfqSessionStatus)) {
			if (this.isSupplierUser && !this.isSaas) {
				endDate.hours(endDate.hours() + CONSTANTS.supplierAuctionEndDateOffset);
			}
		}

		// never convert to before 10am
		if (endDate.hours() < 10) {
			endDate.hours(10);
		}

		let dateDifference = (endDate.valueOf() - now.valueOf()) / 1000;
		const multiplier = dateDifference < 0 ? -1 : 1;

		dateDifference = Math.abs(dateDifference);

		const days = Math.floor(dateDifference / 86400) * multiplier;
		dateDifference -= days * 86400 * multiplier;

		const hours = (Math.floor(dateDifference / 3600) % 24) * multiplier;
		dateDifference -= hours * 3600 * multiplier;

		const minutes = (Math.floor(dateDifference / 60) % 60) * multiplier;
		dateDifference -= minutes * 60 * multiplier;

		const seconds = (dateDifference % 60) * multiplier;

		return { days: Math.max(days, 0), hours: Math.max(hours, 0), minutes: Math.max(minutes, 0), seconds: Math.max(seconds, 0) };
	}

	private getTimeRemainingDisplay(): string {
		if (
			(this.timeRemaining.days > 0 || this.timeRemaining.hours > 0 || this.timeRemaining.minutes > 0 || this.timeRemaining.seconds > 0) &&
			![CONSTANTS.statuses.acomp, CONSTANTS.statuses.signed, CONSTANTS.statuses.confirmed].includes(Number(this.contractStatus))
		) {
			return `${this.timeRemaining.days}d ${this.timeRemaining.hours}h ${this.timeRemaining.minutes}m`;
		}

		return '0d 0h 0m';
	}

	private formatEndDate(): string {
		const endDate = moment(this.endDate);

		if (this.rfqSessionStatus !== 'Extended') {
			if (this.isSupplierUser && !this.isSaas) {
				endDate.hours(endDate.hours() + CONSTANTS.supplierAuctionEndDateOffset);
			}
		}

		// never convert to before 10am
		if (endDate.hours() < 10) {
			endDate.hours(10);
		}

		return endDate.format('ddd, MMM DD, YYYY hh:mm a') + ' CT';
	}

	private formatStartDate(): string {
		return moment(this.startDate).format('ddd, MMM DD, YYYY hh:mm a') + ' CT';
	}

	private formatLastBidDate(): string {
		return this.lastBidDate ? moment(this.lastBidDate).format('ddd, MMM DD, YYYY hh:mm a') + ' CT' : 'No Bid Placed';
	}

	private getRfqSessionStatusColor(): string {
		switch (this.rfqSessionStatus) {
			case 'Won':
				return 'yellow';
			case 'Closed':
				return 'gray';
			case 'New':
				return 'green';
			case 'Ending Soon':
			case 'Extended':
				return 'red';
			case 'In Progress':
				return 'blue';
			default:
				return 'gray';
		}
	}
}
